import { Button } from '@mevaser/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'

/** 'yyyy-mm-dd' from local date parts. Never `toISOString().slice(0, 10)` —
 *  that reads UTC and can land on the wrong day depending on the time of day. */
export function toISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Parses a stored 'yyyy-mm-dd' as a local calendar date. `new Date(str)`
 *  parses it as UTC midnight, which can render one day early in the US. */
export function fromISODate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

export function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}

/** The 6-week, 42-cell grid a month calendar needs: the Sunday on or before
 *  the 1st, through 41 days after that. */
export function gridDays(monthStart: Date): Date[] {
  const start = new Date(monthStart)
  start.setDate(start.getDate() - start.getDay())
  return Array.from({ length: 42 }, (_, i) => {
    const day = new Date(start)
    day.setDate(start.getDate() + i)
    return day
  })
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export interface MonthCalendarEntry {
  /** Unique per pill, e.g. `${id}-due`. */
  key: string
  /** 'yyyy-mm-dd' — the day cell the pill lands in. */
  date: string
  /** The pill itself, usually a router `<Link>`; the grid only lays it out. */
  render: () => ReactNode
}

/** Previous / Today / Next buttons and the month heading. The parent owns the
 *  month, since each page also couples it to its own filters. */
export function MonthNav({
  monthStart,
  onChange,
}: {
  monthStart: Date
  onChange: (next: Date) => void
}) {
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Previous month"
        onClick={() => onChange(addMonths(monthStart, -1))}
      >
        <ChevronLeft />
      </Button>
      <Button variant="secondary" size="sm" onClick={() => onChange(firstOfMonth(new Date()))}>
        Today
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Next month"
        onClick={() => onChange(addMonths(monthStart, 1))}
      >
        <ChevronRight />
      </Button>
      <span className="ms-2 text-sm font-medium">
        {monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
      </span>
    </div>
  )
}

/** A six-week month grid with entries laid out by day, capped per day with a
 *  "+N more" tail. */
export function MonthCalendar({
  monthStart,
  entries,
  maxPerDay = 4,
}: {
  monthStart: Date
  entries: MonthCalendarEntry[]
  maxPerDay?: number
}) {
  const days = useMemo(() => gridDays(monthStart), [monthStart])
  const today = toISODate(new Date())

  const byDay = useMemo(() => {
    const map = new Map<string, MonthCalendarEntry[]>()
    for (const entry of entries) {
      const list = map.get(entry.date) ?? []
      list.push(entry)
      map.set(entry.date, list)
    }
    return map
  }, [entries])

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="grid grid-cols-7 border-b border-border bg-surface-sunken text-2xs font-medium uppercase tracking-wide text-foreground-subtle">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-1.5">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dateKey = toISODate(day)
          const inMonth = day.getMonth() === monthStart.getMonth()
          const isToday = dateKey === today
          const dayEntries = byDay.get(dateKey) ?? []
          const shown = dayEntries.slice(0, maxPerDay)
          const overflow = dayEntries.length - shown.length

          return (
            <div
              key={dateKey}
              className={`min-h-28 border-b border-r border-border p-1.5 last:border-r-0 ${
                inMonth ? '' : 'bg-surface-sunken/50'
              }`}
            >
              <p
                className={`mb-1 text-xs ${
                  isToday
                    ? 'inline-flex size-5 items-center justify-center rounded-full bg-primary font-medium text-primary-foreground'
                    : inMonth
                      ? 'text-foreground-muted'
                      : 'text-foreground-subtle'
                }`}
              >
                {day.getDate()}
              </p>
              <div className="space-y-1">
                {shown.map((entry) => (
                  <div key={entry.key}>{entry.render()}</div>
                ))}
                {overflow > 0 ? (
                  <p className="px-1.5 text-2xs text-foreground-subtle">+{overflow} more</p>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
